import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";

import { useARC } from "providers/ARC/ARCProvider";

import { ConnectionState } from "lib/consts/ARC/connection";
import { isSameLiveMapGeometry } from "lib/navigation/liveMapSync";

import { USE_DUMMY, DUMMY_MAP_INFO, DUMMY_MAP_METADATA, DUMMY_MAP_IMAGE } from "lib/consts/testing";
import { NAVIGATE_TO_POSE_STATUS_TOPIC } from "lib/consts/map";
import { navigationTopicsInfo } from "lib/consts/navigation";
import { parseMapMetaDataMessage } from "lib/protocols/mapFilterYaml";
import { MAP_METADATA_TOPIC } from "lib/protocols/mapFilterYaml";
import { parseNavPathMessage } from "lib/protocols/navPath";
import { hasActiveNavigateToPoseGoal } from "lib/protocols/navigateToPoseStatus";
import { attachRosTopicWithRetry, LATE_ROS_TOPIC_OPTIONS } from "lib/ros/rosTopicSubscribeWithRetry";
import { isCborRawMessage, deserializeCompressedImage } from "lib/ros/cdrDeserializers";
import { FoxgloveControlSource } from "arcjs/control_source/Foxglove";

export const MapContext = createContext();
MapContext.displayName = "Map";
export const useMap = () => useContext(MapContext);

/** Map Editor uses storage BMPs + Fabric; live map frames needlessly rerender it. */
function isMapEditorPathname(pathname) {
    const path = (pathname || "/").replace(/\/+$/, "") || "/";
    return path === "/fleet/map/editor" || path === "/arc/map/editor";
}

const MapProvider = ({ children }) => {
    const { controlSocket, connectionState } = useARC();
    const { pathname } = useLocation();
    const pauseLiveMapImage = isMapEditorPathname(pathname);
    const pauseEditorLiveUpdatesRef = useRef(pauseLiveMapImage);
    pauseEditorLiveUpdatesRef.current = pauseLiveMapImage;

    const [maps, setMaps] = useState([]);
    const [isLoadingMaps, setIsLoadingMaps] = useState(true);
    const [selectedMap, setSelectedMap] = useState(null);
    const [editMap, setEditMap] = useState(null);

    // Live map from ROS topics
    const [mapImageUrl, setMapImageUrl] = useState(null);
    const [mapMetadata, setMapMetadata] = useState(null);
    /** Incremented when displayed map metadata is committed (reload detection fallback). */
    const mapMetadataSeqRef = useRef(0);
    /** Latest `/map_metadata` parse; paired with `/map/compressed` on the control map view. */
    const pendingLiveMapMetadataRef = useRef(null);

    // Robot pose from /robot_pose topic
    const [robotPose, setRobotPose] = useState(null);

    // Planned path from /plan_smoothed (preferred) and /plan (fallback), cleared via navigate_to_pose action status
    const [plannedPath, setPlannedPath] = useState(navigationTopicsInfo.plannedPath.default);
    const navigateGoalActiveRef = useRef(false);
    /** From NavigateToPoseProvider (map/mission UI) when action status topic is unavailable. */
    const navigateGoalFromAppRef = useRef(false);
    /** Latest planned-path geometry per topic; primary (/plan_smoothed) wins when both have data. */
    const plannedPathPointsRef = useRef({ primary: null, fallback: null });
    const syncPlannedPathDisplayRef = useRef(() => { });

    const setNavigateGoalActive = useCallback((active) => {
        const next = Boolean(active);
        if (navigateGoalFromAppRef.current === next) return;
        navigateGoalFromAppRef.current = next;
        syncPlannedPathDisplayRef.current();
    }, []);

    // Legacy map loading (dummy data for DEV mode)
    useEffect(() => {
        if (!controlSocket) return;

        new Promise((resolve, reject) => {
            setIsLoadingMaps(true);
            // await delay(2000)

            if (USE_DUMMY) {
                console.log('using dummy')
                resolve(
                    DUMMY_MAP_INFO.reduce((result, current) => {
                        let _new_current = { ...current, graph: DUMMY_MAP_IMAGE };
                        return [...result, _new_current];
                    }, [])
                );
            } else {
                console.log('not using dummy')
                resolve([]);
            }
        })
            .then((response) => {
                console.log('using response')
                setMaps(response)
            })
            .catch((e) => console.error(e))
            .finally(() => setIsLoadingMaps(false));
    }, [controlSocket]);

    const commitLiveMapFrame = useCallback((imageUrl, metadata) => {
        setMapImageUrl((prevUrl) => {
            if (prevUrl?.startsWith("blob:")) {
                URL.revokeObjectURL(prevUrl);
            }
            return imageUrl;
        });
        if (metadata) {
            setMapMetadata((prev) => {
                if (isSameLiveMapGeometry(prev, metadata)) {
                    return prev;
                }
                mapMetadataSeqRef.current += 1;
                return metadata;
            });
        }
    }, []);

    // Subscribe to /map/compressed; commit bounds together with each JPEG so Leaflet
    // does not stretch stale pixels when /map_metadata ticks faster than the image.
    useEffect(() => {
        if (pauseLiveMapImage) return;

        if (!controlSocket || connectionState !== ConnectionState.Connected) {
            pendingLiveMapMetadataRef.current = null;
            const image = USE_DUMMY ? DUMMY_MAP_IMAGE : null;
            const metadata = USE_DUMMY ? DUMMY_MAP_METADATA : null;
            if (metadata) {
                mapMetadataSeqRef.current = 0;
            }
            setMapImageUrl(image);
            setMapMetadata(metadata);
            return;
        }

        const isFoxglove = controlSocket instanceof FoxgloveControlSource;

        const handleMapCompressed = (rawMsg) => {
            try {
                const msg = isFoxglove
                    ? rawMsg
                    : isCborRawMessage(rawMsg)
                        ? deserializeCompressedImage(rawMsg.bytes)
                        : rawMsg;

                let url;
                if (msg.data instanceof Uint8Array || ArrayBuffer.isView(msg.data)) {
                    const blob = new Blob([msg.data], { type: "image/jpeg" });
                    url = URL.createObjectURL(blob);
                } else {
                    url = "data:image/jpeg;base64," + msg.data;
                }

                const metadata = pendingLiveMapMetadataRef.current;
                commitLiveMapFrame(url, metadata);
            } catch (error) {
                console.error("[/map/compressed] handler ERROR:", error);
            }
        };

        const cleanup = attachRosTopicWithRetry(
            controlSocket,
            "/map/compressed",
            handleMapCompressed,
            {
                initialDelayMs: 1000,
                compression: "cbor-raw",
                markMessageReceived: (msg) =>
                    isCborRawMessage(msg) ||
                    (msg != null && typeof msg === "object" && msg.data != null),
            }
        );

        return () => {
            cleanup();
            pendingLiveMapMetadataRef.current = null;
            setMapImageUrl((prevUrl) => {
                if (prevUrl?.startsWith("blob:")) {
                    URL.revokeObjectURL(prevUrl);
                }
                return null;
            });
            setMapMetadata(null);
            mapMetadataSeqRef.current = 0;
        };
    }, [commitLiveMapFrame, controlSocket, connectionState, pauseLiveMapImage]);

    // Subscribe to /map_metadata for map dimensions and origin (Aurora slamware_ros_sdk).
    // On the control map view, store pending geometry only; Map Editor still applies live ticks.
    useEffect(() => {
        if (!controlSocket || connectionState !== ConnectionState.Connected) {
            pendingLiveMapMetadataRef.current = null;
            if (!pauseLiveMapImage) {
                return;
            }
            const metadata = USE_DUMMY ? DUMMY_MAP_METADATA : null;
            mapMetadataSeqRef.current = 0;
            setMapMetadata(metadata);
            return;
        }

        const handleMapMetadata = (mapMetaDataMsg) => {
            if (pauseEditorLiveUpdatesRef.current) return;
            try {
                const metadata = parseMapMetaDataMessage(mapMetaDataMsg);
                if (!metadata) return;

                if (pauseLiveMapImage) {
                    setMapMetadata((prev) => {
                        if (isSameLiveMapGeometry(prev, metadata)) {
                            return prev;
                        }
                        mapMetadataSeqRef.current += 1;
                        return metadata;
                    });
                    return;
                }

                if (isSameLiveMapGeometry(pendingLiveMapMetadataRef.current, metadata)) {
                    return;
                }
                pendingLiveMapMetadataRef.current = metadata;
            } catch (error) {
                console.error("Error processing map metadata:", error);
            }
        };

        const cleanup = attachRosTopicWithRetry(
            controlSocket,
            MAP_METADATA_TOPIC,
            handleMapMetadata,
            {
                markMessageReceived: (msg) =>
                    parseMapMetaDataMessage(msg) != null,
            }
        );

        return () => {
            cleanup();
            pendingLiveMapMetadataRef.current = null;
        };
    }, [controlSocket, connectionState, pauseLiveMapImage]);

    // Subscribe to /robot_pose topic for robot position
    useEffect(() => {
        if (!controlSocket || connectionState !== ConnectionState.Connected) {
            // Only clear robotPose when actually disconnected
            if (connectionState !== ConnectionState.Connected) {
                setRobotPose(null);
            }
            return;
        }

        const handleRobotPose = (poseStampedMsg) => {
            if (pauseEditorLiveUpdatesRef.current) return;
            if (!poseStampedMsg || !poseStampedMsg.pose) {
                return;
            }

            const pose = {
                position: poseStampedMsg.pose.position,
                orientation: poseStampedMsg.pose.orientation
            };

            setRobotPose(pose);
        };

        const cleanup = attachRosTopicWithRetry(
            controlSocket,
            "/robot_pose",
            handleRobotPose,
            {
                initialDelayMs: 1000,
                markMessageReceived: (msg) =>
                    msg != null && typeof msg === "object" && msg.pose != null,
            }
        );

        return () => cleanup();
    }, [controlSocket, connectionState]);

    // Subscribe to both /plan_smoothed and /plan; prefer smoothed when it publishes
    useEffect(() => {
        const { topics: planTopics, default: defaultPath } = navigationTopicsInfo.plannedPath;
        const [primaryPlanTopic, fallbackPlanTopic] = planTopics;

        const clearPlannedPath = () => {
            plannedPathPointsRef.current = { primary: null, fallback: null };
            setPlannedPath(defaultPath);
        };

        const pickPlannedPathPoints = () => {
            const { primary, fallback } = plannedPathPointsRef.current;
            if (primary && primary.length >= 2) {
                return primary;
            }
            if (fallback && fallback.length >= 2) {
                return fallback;
            }
            return null;
        };

        const isNavigateGoalActive = () =>
            navigateGoalActiveRef.current || navigateGoalFromAppRef.current;

        const syncPlannedPathDisplay = () => {
            if (pauseEditorLiveUpdatesRef.current) return;
            const points = pickPlannedPathPoints();
            const active = isNavigateGoalActive();

            setPlannedPath(active && points ? points : defaultPath);
        };

        syncPlannedPathDisplayRef.current = syncPlannedPathDisplay;

        if (!controlSocket || connectionState !== ConnectionState.Connected) {
            navigateGoalActiveRef.current = false;
            navigateGoalFromAppRef.current = false;
            if (connectionState !== ConnectionState.Connected) {
                clearPlannedPath();
            }
            return;
        }

        const cleanups = [];

        const handleNavigateStatus = (statusMsg) => {
            const active = hasActiveNavigateToPoseGoal(statusMsg);
            navigateGoalActiveRef.current = active;
            if (!active && !navigateGoalFromAppRef.current) {
                plannedPathPointsRef.current = { primary: null, fallback: null };
            }
            syncPlannedPathDisplay();
        };

        const handlePlanFromTopic = (source) => (pathMsg) => {
            const points = parseNavPathMessage(pathMsg);
            plannedPathPointsRef.current[source] = points.length >= 2 ? points : null;
            syncPlannedPathDisplay();
        };

        const handlePrimaryPlan = handlePlanFromTopic("primary");
        const handleFallbackPlan = handlePlanFromTopic("fallback");

        const planTopicOptions = {
            initialDelayMs: 1000,
            compression: "none",
            ...LATE_ROS_TOPIC_OPTIONS,
            markMessageReceived: (msg) => parseNavPathMessage(msg).length >= 2,
        };

        const statusTopicOptions = {
            initialDelayMs: 1000,
            compression: "none",
            ...LATE_ROS_TOPIC_OPTIONS,
            subscribeQos: {
                durability: "transient_local",
                reliability: "reliable",
                history: "keep_last",
                depth: 10,
            },
            markMessageReceived: (msg) => Array.isArray(msg?.status_list),
        };

        cleanups.push(
            attachRosTopicWithRetry(controlSocket, primaryPlanTopic, handlePrimaryPlan, planTopicOptions),
        );
        cleanups.push(
            attachRosTopicWithRetry(controlSocket, fallbackPlanTopic, handleFallbackPlan, planTopicOptions),
        );

        /** Fallback: arcjs proxy subscribe (pre-retry path) in case roslib Topic + cbor misses messages. */
        const planDirectDelayId = setTimeout(() => {
            if (!controlSocket || connectionState !== ConnectionState.Connected) {
                return;
            }
            try {
                controlSocket.subscribe(primaryPlanTopic, handlePrimaryPlan);
                cleanups.push(() => {
                    try {
                        controlSocket.unsubscribe(primaryPlanTopic, handlePrimaryPlan);
                    } catch {
                        /* ignore */
                    }
                });
                controlSocket.subscribe(fallbackPlanTopic, handleFallbackPlan);
                cleanups.push(() => {
                    try {
                        controlSocket.unsubscribe(fallbackPlanTopic, handleFallbackPlan);
                    } catch {
                        /* ignore */
                    }
                });
            } catch {
                /* ignore */
            }
        }, 1000);
        cleanups.push(() => clearTimeout(planDirectDelayId));

        cleanups.push(
            attachRosTopicWithRetry(
                controlSocket,
                NAVIGATE_TO_POSE_STATUS_TOPIC,
                handleNavigateStatus,
                statusTopicOptions,
            ),
        );

        const statusDirectDelayId = setTimeout(() => {
            if (!controlSocket || connectionState !== ConnectionState.Connected) return;
            try {
                controlSocket.subscribe(NAVIGATE_TO_POSE_STATUS_TOPIC, handleNavigateStatus);
                cleanups.push(() => {
                    try {
                        controlSocket.unsubscribe(NAVIGATE_TO_POSE_STATUS_TOPIC, handleNavigateStatus);
                    } catch {
                        /* ignore */
                    }
                });
            } catch {
                /* ignore */
            }
        }, 1000);
        cleanups.push(() => clearTimeout(statusDirectDelayId));

        return () => {
            navigateGoalActiveRef.current = false;
            plannedPathPointsRef.current = { primary: null, fallback: null };
            cleanups.forEach((cleanup) => cleanup());
        };
    }, [controlSocket, connectionState]);

    const mapContextValue = useMemo(
        () => ({
            maps,
            isLoadingMaps,
            selectedMap,
            setSelectedMap,
            // Live map data
            mapImageUrl,
            mapMetadata,
            mapMetadataSeqRef,
            // Robot pose
            robotPose,
            // Nav2 planned path
            plannedPath,
            setNavigateGoalActive,
        }),
        [
            maps,
            isLoadingMaps,
            selectedMap,
            mapImageUrl,
            mapMetadata,
            robotPose,
            plannedPath,
            setNavigateGoalActive,
        ],
    );

    return (
        <MapContext.Provider value={mapContextValue}>
            {children}
        </MapContext.Provider>
    );
};

export default MapProvider;
